import { EnvironmentInjector, Injectable, inject } from '@angular/core';
import { fromJson, toJson } from '@bufbuild/protobuf';
import { NbToastrService } from '@nebular/theme';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  CollectionNames,
  TimeTrackingServiceCreateResponseJson,
  TimeTrackingServiceCreateResponseSchema,
  TimeTrackingServiceListRequestJson,
  TimeTrackingServiceListRequestSchema,
  defaultApiSerializeJsonOptions,
} from '@vmspdb/definitions';
import { GlobalErrorHandlerService } from '@vmspdb/ui/common';
import { AuthRunEffects } from '@vmspdb/ui/store';
import { EMPTY, Observable, of, tap } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';
import {
  TimeTrackingRepositoryService,
  withSearchSync,
} from '../../repositories';
import { UtilitiesFacade } from '../utilities/utilities.facade';
import { PaginatedTimeTrackingActions } from './paginated-time-tracking.actions';

@Injectable()
export class PaginatedTimeTrackingEffects extends AuthRunEffects {
  private readonly injector = inject(EnvironmentInjector);
  private readonly errors = inject(GlobalErrorHandlerService);
  private readonly repository = inject(TimeTrackingRepositoryService);
  private readonly notifications = inject(NbToastrService);
  private readonly utilities = inject(UtilitiesFacade);

  listPaginatedTimeTrackings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        PaginatedTimeTrackingActions.listPaginatedTimeTrackings,
        PaginatedTimeTrackingActions.cancel,
      ),
      switchMap((action) =>
        action.type === PaginatedTimeTrackingActions.cancel.type
          ? EMPTY
          : of(action).pipe(this.performSearch()),
      ),
    );
  });

  performSearch() {
    return (
      source: Observable<{
        params: TimeTrackingServiceListRequestJson;
      }>,
    ) =>
      source.pipe(
        debounceTime(250),
        withSearchSync(CollectionNames.TimeTracking, this.injector),
        tap(() => this.utilities.queue()),
        switchMap((payload) => {
          return this.repository.list(
            fromJson(TimeTrackingServiceListRequestSchema, payload.params),
          );
        }),
        tap((data) => {
          if (data.degraded) {
            this.errors.reportSearchDegradation(CollectionNames.TimeTracking);
            this.notifications.warning(
              'There was an error with the search server for fetching time tracking records.',
              'Search',
              {
                duration: 5000,
              },
            );
          }
        }),
        map((data) =>
          PaginatedTimeTrackingActions.listPaginatedTimeTrackingsSuccess({
            data: {
              ...data,
              items: (data.items ?? []).map(
                (item) =>
                  toJson(
                    TimeTrackingServiceCreateResponseSchema,
                    item,
                    defaultApiSerializeJsonOptions,
                  ) as TimeTrackingServiceCreateResponseJson,
              ),
            },
          }),
        ),
        catchError((error) => {
          this.errors.reportCaughtError(error);
          return of(
            PaginatedTimeTrackingActions.listPaginatedTimeTrackingsFailure(
              error,
            ),
          );
        }),
        tap(() => this.utilities.complete()),
      );
  }

  constructor() {
    super(inject(Actions));
  }
}
